import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn } from "@/lib/utils";
import type { ActivityKind, ActivityLog, ActivityStatus } from "@/data/types";

type SortKey = "scheduled-asc" | "scheduled-desc" | "kind";
type StatusFilter = "all" | ActivityStatus;
type KindFilter = "all" | ActivityKind;
const PAGE_SIZE = 7;

const STATUS_LABEL: Record<ActivityStatus, string> = {
  scheduled: "Programada",
  completed: "Finalizada",
  missed: "Não realizada",
  "on-time": "No prazo",
};

const STATUS_CHIP: Record<ActivityStatus, string> = {
  scheduled: "bg-chart-1/15 text-chart-1",
  completed: "bg-chart-4/15 text-chart-4",
  missed: "bg-danger/15 text-danger",
  "on-time": "bg-chart-3/15 text-chart-3",
};

const KIND_LABEL: Record<ActivityKind, string> = {
  call: "Ligação",
  meeting: "Reunião",
  "follow-up": "Follow-up",
  proposal: "Proposta",
  visit: "Visita",
};

const KIND_ICON: Record<ActivityKind, string> = {
  call: "📞",
  meeting: "👥",
  "follow-up": "✉️",
  proposal: "📄",
  visit: "📍",
};

export function ActivityDaySection({
  date,
  onClear,
}: {
  date: string | null;
  onClear: () => void;
}) {
  const [sortBy, setSortBy] = useState<SortKey>("scheduled-asc");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const q = useQuery({
    queryKey: ["activity-day", date],
    queryFn: () => client.getActivityDayDetail({ date: date! }),
    enabled: !!date,
  });

  useEffect(() => {
    setSortBy("scheduled-asc");
    setStatusFilter("all");
    setKindFilter("all");
    setAssigneeFilter("all");
    setSearch("");
    setPage(1);
  }, [date]);

  useEffect(() => {
    setPage(1);
  }, [sortBy, statusFilter, kindFilter, assigneeFilter, search]);

  const detail = q.data;

  const assigneeOptions = useMemo(() => {
    const set = new Set<string>();
    (detail?.activities ?? []).forEach((a) => set.add(a.assignedUserName));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [detail]);

  const visible = useMemo(() => {
    if (!detail) return [];
    const filtered = detail.activities.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (kindFilter !== "all" && a.kind !== kindFilter) return false;
      if (assigneeFilter !== "all" && a.assignedUserName !== assigneeFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!a.title.toLowerCase().includes(s) && !a.contactName.toLowerCase().includes(s)) return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sortBy === "scheduled-desc") return b.scheduledAt.localeCompare(a.scheduledAt);
      if (sortBy === "kind") return a.kind.localeCompare(b.kind);
      return a.scheduledAt.localeCompare(b.scheduledAt);
    });
  }, [detail, sortBy, statusFilter, kindFilter, assigneeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  );

  if (!date) {
    return (
      <section className="rounded-lg border border-dashed border-border-subtle bg-bg-card/50 p-8 text-center">
        <div className="mx-auto max-w-md space-y-1.5">
          <div className="text-sm font-medium text-text-secondary">Atividades do período</div>
          <p className="text-[13px] text-text-muted">
            Clique em qualquer dia do gráfico acima para ver todas as atividades programadas e executadas nesse dia.
          </p>
        </div>
      </section>
    );
  }

  const dateLabel = new Date(date).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-[0.15em] text-brand-amber">
            Atividades do período
          </div>
          <h2 className="text-lg font-semibold capitalize tracking-tight text-text-primary">{dateLabel}</h2>
          {detail && (
            <p className="text-[13px] text-text-muted">
              <span className="font-semibold text-chart-1">{detail.totals.scheduled}</span> programadas ·
              <span className="ml-1 font-semibold text-chart-4">{detail.totals.completed}</span> finalizadas ·
              <span className="ml-1 font-semibold text-chart-3">{detail.totals.onTime}</span> no prazo ·
              <span className="ml-1 font-semibold text-danger">{detail.totals.missed}</span> não realizadas
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-border-strong hover:text-text-primary"
        >
          <span>×</span>
          <span>Limpar dia</span>
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-6 py-3">
        <input
          type="search"
          placeholder="Buscar por título ou contato…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 flex-1 min-w-[220px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="all">Todos</option>
            <option value="scheduled">Programadas</option>
            <option value="completed">Finalizadas</option>
            <option value="on-time">No prazo</option>
            <option value="missed">Não realizadas</option>
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Tipo:</span>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="all">Todos</option>
            {(Object.keys(KIND_LABEL) as ActivityKind[]).map((k) => (
              <option key={k} value={k}>{KIND_LABEL[k]}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Responsável:</span>
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            {assigneeOptions.map((a) => (
              <option key={a} value={a}>{a === "all" ? "Todos" : a}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Ordenar:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="scheduled-asc">Horário ↑</option>
            <option value="scheduled-desc">Horário ↓</option>
            <option value="kind">Tipo A–Z</option>
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {detail?.activities.length ?? 0}
        </div>
      </div>

      <div>
        {q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">Carregando atividades…</div>
        )}
        {detail && visible.length === 0 && !q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">
            Nenhuma atividade encontrada com os filtros atuais.
          </div>
        )}
        {visible.length > 0 && (
          <>
            <table className="w-full">
              <thead className="bg-bg-secondary">
                <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
                  <th className="px-5 py-3 text-left">Atividade</th>
                  <th className="px-5 py-3 text-left">Responsável</th>
                  <th className="px-5 py-3 text-left">Horário</th>
                  <th className="px-5 py-3 text-right">Duração</th>
                  <th className="px-5 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((a, i) => (
                  <ActivityRow key={a.activityId} activity={a} isLast={i === pageItems.length - 1} />
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <Paginator
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                rangeStart={(page - 1) * PAGE_SIZE + 1}
                rangeEnd={Math.min(page * PAGE_SIZE, visible.length)}
                total={visible.length}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function ActivityRow({ activity, isLast }: { activity: ActivityLog; isLast: boolean }) {
  const scheduledTime = new Date(activity.scheduledAt).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-lg leading-none" aria-hidden>{KIND_ICON[activity.kind]}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{activity.title}</span>
              <span className="text-[10px] uppercase tracking-wider text-text-muted">{KIND_LABEL[activity.kind]}</span>
            </div>
            <div className="text-xs text-text-muted">
              com <span className="text-text-secondary">{activity.contactName}</span>
            </div>
            {activity.notes && (
              <div className="mt-1 truncate text-xs italic text-text-muted">"{activity.notes}"</div>
            )}
            {activity.tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {activity.tags.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center gap-1 rounded-full bg-bg-primary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                  >
                    <span className="h-1 w-1 rounded-full" style={{ backgroundColor: t.color }} />
                    {t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-text-secondary">{activity.assignedUserName}</td>
      <td className="px-5 py-3 text-text-secondary">{scheduledTime}</td>
      <td className="px-5 py-3 text-right text-text-secondary">
        {activity.durationMinutes ? `${activity.durationMinutes} min` : "—"}
      </td>
      <td className="px-5 py-3">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", STATUS_CHIP[activity.status])}>
          {STATUS_LABEL[activity.status]}
        </span>
      </td>
    </tr>
  );
}

function Paginator({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart: number;
  rangeEnd: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
      <span className="text-xs text-text-muted">
        Exibindo <span className="font-semibold text-text-primary">{rangeStart}–{rangeEnd}</span> de {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerButton label="‹" disabled={page === 1} onClick={() => onChange(page - 1)} />
        {buildPageWindow(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-2 text-xs text-text-muted">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={cn(
                "min-w-[30px] rounded-md px-2 py-1 text-xs font-medium transition",
                p === page
                  ? "bg-brand-amber text-text-on-accent"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
              )}
            >
              {p}
            </button>
          )
        )}
        <PagerButton label="›" disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

function PagerButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
        disabled
          ? "cursor-not-allowed text-text-muted/40"
          : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
      )}
    >
      {label}
    </button>
  );
}

function buildPageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}
